import {
  disclosure,
  disclosureCss,
  kitPartCss,
  microHeading,
  microHeadingCss,
  positive,
  rowList,
  rowsCss,
} from '../armKit';
import { deepWorkFacts } from '../pageData';
import {
  compactIdentityCss,
  cssRule,
  HAIRLINE,
  INK,
  matchesBreakpoint,
  mediaCss,
  sectionChips,
} from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { SectionChip } from '../surface';

type Door = { name: string; count: number | null; href: string };

const HUB_CLASS = 'profile-liautotest-hub';
const DOOR_CLASS = `${HUB_CLASS}__door`;
const TITLE = 'On this profile';
// The nav's "all" chip is the page the visitor is already on, so a door to it
// is a door to nowhere.
const ALL_CHIP = 'all';
const DOOR_SOURCE = 'profile-liautotest-hub-door';
const SEE_ALL_SOURCE = `${DOOR_SOURCE}-all`;
const WORK_SOURCE = 'profile-liautotest-hub-work';
// Eight compact rows is a phone screen's worth; a section longer than that
// keeps its tail behind the See-all navigation rather than under the thumb.
const ROW_CAP = 8;

function doorsOf(chips: SectionChip[]): Door[] {
  return chips.flatMap((chip) =>
    chip.href == null || chip.name === '' || chip.name === ALL_CHIP
      ? []
      : [{ name: chip.name, count: chip.count, href: chip.href }],
  );
}

function menuCss(scope: string): string {
  return (
    cssRule([`${scope} .${HUB_CLASS}`], 'padding: 0 16px 12px;') +
    microHeadingCss(scope) +
    rowsCss(scope, { itemPadding: '4px 0' }) +
    disclosureCss(scope) +
    // Same specificity as the kit's summary rule, injected after it, so
    // document order is what turns the quiet inline toggle into a full-width
    // tappable row.
    kitPartCss(
      'summary',
      scope,
      'display: block; width: 100%; padding: 10px 0; ' +
        `font-size: 14px; font-weight: 600; color: ${INK};`,
    ) +
    cssRule(
      [`${scope} .${DOOR_CLASS} + .${DOOR_CLASS}`],
      `border-top: 1px solid ${HAIRLINE};`,
    ) +
    // The menu is DOM, so unlike the compaction band it cannot expire on its
    // own; this is what a resized-wide viewport sees instead of it.
    mediaCss('wide', cssRule([`${scope} .${HUB_CLASS}`], 'display: none;'))
  );
}

// The count comes off the chip, never off the fetched facts: the chip's number
// is the corpus-wide truth the server printed, and the facts map is a capped
// fetch that undercounts exactly the biggest profiles.
function doorLabel(door: Door): string {
  const count = positive(door.count);

  return count == null ? door.name : `${door.name} (${count.toLocaleString()})`;
}

function doorRows(
  door: Door,
  facts: Map<number, WorkFacts> | null,
  ctx: ArmContext,
): HTMLElement {
  const matches =
    facts == null
      ? []
      : [...facts].flatMap(([workId, fact]) =>
          fact.section === door.name && fact.title != null && fact.url != null
            ? [{ workId, title: fact.title, url: fact.url }]
            : [],
        );

  const rows: Row[] = matches.slice(0, ROW_CAP).map((work) => ({
    title: work.title,
    url: work.url,
    onClick: () => ctx.track(WORK_SOURCE, { entityId: work.workId }),
  }));

  // The See-all row is the door's floor, never its filler: it appears whenever
  // the rows above cannot be the whole section — no facts yet, a corpus past
  // the fetch's 300-work cap, or a tail this cap trimmed — and a door whose
  // section matched nothing keeps it as its one row rather than opening onto
  // an empty box.
  const count = positive(door.count);
  if (
    rows.length === 0 ||
    matches.length > ROW_CAP ||
    (count != null && rows.length < count)
  ) {
    rows.push({
      title: `See all ${door.name}`,
      url: door.href,
      onClick: () =>
        ctx.track(SEE_ALL_SOURCE, { extra: { section: door.name } }),
    });
  }

  return rowList(rows);
}

function menuFor(doors: Door[], ctx: ArmContext): HTMLElement {
  const root = document.createElement('div');
  root.className = HUB_CLASS;
  root.appendChild(microHeading(TITLE));

  // The facts fetch waits for the first tap on ANY door — a visitor who never
  // opens one costs no request — and every door shares the one promise
  // pageData memoizes.
  let facts: Promise<Map<number, WorkFacts>> | null = null;
  const factsOnFirstTap = (): Promise<Map<number, WorkFacts>> =>
    (facts ??= deepWorkFacts(ctx.viewedUserId));

  // A door expands IN PLACE: the first tap is a reveal, not a navigation, and
  // only the work rows and the See-all row inside it leave the page. Until the
  // fetch settles an opened door shows just its See-all row, then the rows
  // paint in where the visitor is already looking.
  doors.forEach((door) => {
    const content = document.createElement('div');
    content.appendChild(doorRows(door, null, ctx));

    const wrap = document.createElement('div');
    wrap.className = DOOR_CLASS;
    wrap.appendChild(
      disclosure(doorLabel(door), content, () => {
        ctx.track(DOOR_SOURCE, { extra: { section: door.name } });
        void factsOnFirstTap().then((map) => {
          content.replaceChildren(doorRows(door, map, ctx));
        });
      }),
    );
    root.appendChild(wrap);
  });

  return root;
}

export const phoneProfileHub: ArmSpec = {
  // The same call the gate makes, so the prediction and the render cannot
  // disagree about which band the pageview loaded in.
  stamp: () => ({
    render_state: matchesBreakpoint('narrow') ? 'rendered' : 'fallback',
  }),

  onReady: (ctx) => {
    if (!matchesBreakpoint('narrow')) return;

    // CSS-only on the identity block — no DOM moves — which is what makes the
    // band safe: nothing re-runs on resize, and a resized-wide viewport drops
    // out of the band into control exactly. Follow and Message keep their
    // full-size targets; that is the recipe's own guarantee.
    ctx.injectStyles(mediaCss('narrow', compactIdentityCss(ctx.scope)));

    const doors = doorsOf(sectionChips());
    const slot = doors.length === 0 ? null : ctx.mountSlot('railTop');
    if (slot == null) {
      // Compaction landed and the door menu did not — a nav with no usable
      // chips, or no rail anchor to mount under.
      ctx.markRendered('partial');
      return;
    }

    // railTop: on narrow the rail stacks first, so the menu sits directly
    // under the compact identity row and above the papers.
    ctx.injectStyles(menuCss(ctx.scope));
    slot.appendChild(menuFor(doors, ctx));
    ctx.markRendered();
  },
};
